import { PostCategory } from '@/entities';
import { ApiConstructor, AppConstructor } from '@/shared/components/constructor/Constructor';
import { formatDate } from '@/shared/lib/utils/dates';
import { axiosAPI } from './axios-instance';
import { ApiMeta, ApiRequestFnParams, ApiResponse } from './lib/types';
import { transformConstructorFromApi } from './lib/utils/transform-constructor';

export type ApiNewsDetailRequest = Partial<{
    slug: string;
}>;

type Params = ApiRequestFnParams<ApiNewsDetailRequest> & {
    postCategories: PostCategory[];
};

export type ApiNewsDetailResponse = ApiResponse<{
    id: number;
    slug: string;
    name: string;
    published_at: string;
    updated_at?: string;
    categoryIds: number[];
    description?: string;
    content: ApiConstructor;
    meta: ApiMeta;
}>;

export type AppNewsDetailResponse = ApiResponse<{
    id: number;
    content: AppConstructor;
    title: string;
    description: string;
    categories: string[];
    slug: string;
    date: string;
    publishedAtIso?: string;
    updatedAtIso?: string;
    meta: ApiMeta;
}>;

const transformApiResponse = (
    response: ApiNewsDetailResponse,
    postCategories: PostCategory[],
): AppNewsDetailResponse => {
    const data = response.data!;

    const publishedAtIso = data.published_at ? new Date(data.published_at).toISOString() : undefined;
    const updatedAtIso = data.updated_at ? new Date(data.updated_at).toISOString() : publishedAtIso;

    return {
        ...response,
        data: {
            id: data.id,
            categories: (data.categoryIds || []).map(
                (id) => postCategories.find((category) => category.id === id)?.name || '',
            ),
            content: transformConstructorFromApi(data.content || []),
            date: data.published_at ? formatDate(new Date(data.published_at)) : '',
            description: data.description || '',
            slug: data.slug || '',
            title: data.name || '',
            publishedAtIso,
            updatedAtIso,
            meta: data.meta || {},
        },
    };
};

export const getNewsDetail = ({ params = {}, postCategories }: Params): Promise<AppNewsDetailResponse> =>
    axiosAPI
        .get<ApiNewsDetailResponse>(`/v1/posts/${encodeURIComponent(params.slug || '')}`, { params })
        .then((res) => transformApiResponse(res.data, postCategories));
